import React, { useEffect, useState } from 'react';
import { DashboardCard } from '../DashboardCard';
import expiringSvg from '@/assets/icons-normalized/expiring.svg?raw';
import arrowRightSvg from '@/assets/icons-normalized/arrow-right.svg?raw';
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from '@shelflix/ui-web/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { cn } from '@/utils/utils';
import AppIcon from '@shelflix/ui-web/components/AppIcon';
import Loader from '@/components/Loader';

const formatDate = (dateString: string | null) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

export interface ExpiringCampaignsItem {
  group: string;
  brand: string;
  extra: {
    offlineCount: number;
    unavailableCount: number;
  };
  expiringDate: string | null;
}

export interface ExpiringCampaignsProps {
  expiringCampaignsData: {
    total: number;
    campaignsCount: ExpiringCampaignsItem[];
  };
  brandOptions: string[];
  brandFilter: string;
  onBrandFilterChange: (value: string) => void;
  expirationFilter: string;
  onExpirationFilterChange: (value: string) => void;
  page?: number;
  onPageChange?: (page: number) => void;
  loading?: boolean;
  className?: string;
}

export function ExpiringCampaigns({
  expiringCampaignsData,
  brandOptions,
  brandFilter,
  onBrandFilterChange,
  expirationFilter,
  onExpirationFilterChange,
  page,
  onPageChange,
  loading = false,
  className,
}: ExpiringCampaignsProps) {
  const expirationOptions = [
    { value: 'closest', label: 'Mais próximos' },
    { value: 'farthest', label: 'Mais distantes' },
  ];

  const [filteredData, setFilteredData] = useState<ExpiringCampaignsItem[]>(expiringCampaignsData.campaignsCount);
  const currentPage = page || 1;
  const itemsPerPage = 10;

  useEffect(() => {
    if (brandFilter === 'all') {
      setFilteredData(expiringCampaignsData.campaignsCount);
    } else {
      setFilteredData(expiringCampaignsData.campaignsCount.filter((item) => item.brand === brandFilter));
    }
  }, [expiringCampaignsData, brandFilter, expirationFilter]);

  // Calcular paginação
  const totalPages = Math.ceil(expiringCampaignsData.total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, expiringCampaignsData.total);

  // Gerar array de páginas para exibir
  const getPageButtons = () => {
    const buttons: (number | string)[] = [];

    if (totalPages <= 5) {
      // Se tem 5 ou menos páginas, mostra todas
      for (let i = 1; i <= totalPages; i++) {
        buttons.push(i);
      }
    } else {
      // totalPages > 5
      const hasBefore = currentPage > 1;
      const hasAfter = currentPage < totalPages;

      if (hasBefore && hasAfter) {
        // Página atual tem espaço antes e depois
        // Manter página atual como segundo botão
        const firstPageInRange = Math.max(1, currentPage - 1);
        const lastPageInRange = Math.min(totalPages, firstPageInRange + 1);

        // Adicionar páginas do intervalo
        for (let i = firstPageInRange; i <= lastPageInRange; i++) {
          buttons.push(i);
        }

        // Adicionar fim se necessário
        if (lastPageInRange < totalPages) {
          buttons.push('...');
          buttons.push(totalPages);
        } else {
          buttons.push(totalPages);
        }
      } else if (!hasBefore) {
        // Estamos no começo (página 1)
        buttons.push(1, 2, 3, '...', totalPages);
      } else {
        // Estamos no fim
        buttons.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      }
    }

    return buttons.slice(0, 5); // Garantir máximo 5 elementos
  };

  // Funções de navegação
  const handlePageChange = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages && onPageChange) {
      onPageChange(pageNumber);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages && onPageChange) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <DashboardCard
      title="Campanhas próximas a vencer"
      icon={expiringSvg}
      className={cn('h-152 max-h-152 bg-[#0E0E0E]', className)}
    >
      <div className="grid w-full grid-cols-2 justify-items-stretch gap-4">
        <div className="flex flex-col gap-1">
          <span className="align-middle text-xs leading-[125%] font-normal text-[#B2A598]">Marca</span>
          <Select value={brandFilter} onValueChange={onBrandFilterChange}>
            <SelectTrigger className="w-full rounded-[5px] border-[#332F2B] text-[#B2A598] dark:bg-[#1717171A] dark:hover:bg-[#17171780]">
              <SelectValue placeholder="Filtrar por marca" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {brandOptions.map((brand) => (
                <SelectItem key={brand} value={brand}>
                  {brand}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <span className="align-middle text-xs leading-[125%] font-normal text-[#B2A598]">Vencimento</span>
          <Select value={expirationFilter} onValueChange={onExpirationFilterChange}>
            <SelectTrigger className="w-full rounded-[5px] border-[#332F2B] text-[#B2A598] dark:bg-[#1717171A] dark:hover:bg-[#17171780]">
              <SelectValue placeholder="Filtrar por vencimento" />
            </SelectTrigger>
            <SelectContent>
              {expirationOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {loading ? (
        <Loader message="Carregando campanhas..." />
      ) : (
        <>
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[38%] overflow-hidden font-normal text-[#B2A598]">Material</TableHead>
                <TableHead className="w-[17%] overflow-hidden font-normal whitespace-normal text-[#B2A598]">
                  Data de Vencimento
                </TableHead>
                <TableHead className="w-[15%] overflow-hidden font-normal whitespace-normal text-[#B2A598]">
                  Dias Restantes
                </TableHead>
                <TableHead className="w-[15%] overflow-hidden font-normal text-[#B2A598]">Offline</TableHead>
                <TableHead className="w-[15%] overflow-hidden font-normal text-[#B2A598]">Indisp.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="truncate text-xs text-[#F2E6DA]">{item.group}</TableCell>
                  <TableCell
                    className={cn(`truncate text-xs`, item.expiringDate ? 'text-[#F2E6DA]' : 'text-[#736A61]')}
                  >
                    {item.expiringDate ? formatDate(item.expiringDate) : 'Não Possui'}
                  </TableCell>
                  <TableCell
                    className={cn(`truncate text-xs`, item.expiringDate ? 'text-[#F2E6DA]' : 'text-[#736A61]')}
                  >
                    {item.expiringDate
                      ? (() => {
                          const daysRemaining = Math.ceil(
                            (new Date(item.expiringDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
                          );
                          return daysRemaining <= 0 ? 'Vencido' : daysRemaining;
                        })()
                      : '-'}
                  </TableCell>
                  <TableCell className="truncate text-xs text-[#CC4747]">{item.extra.offlineCount}</TableCell>
                  <TableCell className="truncate text-xs text-[#33AEFF]">{item.extra.unavailableCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
      <div className="mt-auto flex w-full items-center gap-4 px-4">
        <span className="mr-auto text-xs text-[#B4B4B4]">
          Mostrando {totalPages === 0 ? 0 : startIndex + 1}-{endIndex} de {expiringCampaignsData.total} campanhas
        </span>
        <div className="flex gap-2.5">
          {getPageButtons().map((pageNum, index) =>
            pageNum === '...' ? (
              <div
                key={`ellipsis-${index}`}
                className="flex h-8 w-8 items-center justify-center text-xs text-[#736A61]"
              >
                ...
              </div>
            ) : (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum as number)}
                disabled={totalPages === 0}
                className={cn(
                  'h-8 w-8 rounded-[5px] border text-xs transition-colors',
                  currentPage === pageNum
                    ? 'bg-primary border-primary text-white'
                    : 'border-[#736A61] bg-[#191919] text-[#736A61] hover:border-[#B2A598] hover:text-[#B2A598]'
                )}
              >
                {pageNum}
              </button>
            )
          )}
        </div>
        <button
          onClick={handleNextPage}
          disabled={currentPage >= totalPages || totalPages === 0}
          className="flex h-8 items-center gap-1 rounded-[5px] bg-[#191919] px-2 text-xs text-[#F2E6DA] hover:bg-[#282828] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Proximo
          <AppIcon
            source="svg"
            name="right-arrow"
            svgContent={arrowRightSvg}
            size={16}
            weight={900}
            colorStyle={{ type: 'solid', color: '#F2E6DA' }}
          />
        </button>
      </div>
    </DashboardCard>
  );
}
